'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import numerologyData from '../lib/numerology-data.json';

type NumerologyItem = {
  number: number;
  keywords: string;
  description: string;
};

// Быстрая таблица: number -> item (собирается один раз)
const NUM_MAP: Record<number, NumerologyItem> = Object.fromEntries(
  (numerologyData as NumerologyItem[]).map((x) => [x.number, x]),
);

// --- helpers ---
function reduceToSingleDigit(num: number): number {
  let n = num;
  while (n > 9) {
    let s = 0;
    for (const ch of String(n)) s += ch.charCodeAt(0) - 48;
    n = s;
  }
  return n;
}

function getTodayParts(timeZone = 'Asia/Tomsk') {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const y = Number(parts.find((p) => p.type === 'year')?.value ?? '2000');
  const m = Number(parts.find((p) => p.type === 'month')?.value ?? '01');
  const d = Number(parts.find((p) => p.type === 'day')?.value ?? '01');
  return { y, m, d };
}

function toYMDNumber(y: number, m: number, d: number) {
  return y * 10000 + m * 100 + d;
}

function getPersonalDayNumber(birthISO: string, timeZone = 'Asia/Tomsk'): number {
  // birthISO: "YYYY-MM-DD"
  const [birthYear, birthMonth, birthDay] = birthISO.split('-').map(Number);
  if (!birthYear || !birthMonth || !birthDay) return 0;

  const { y: currentYear, m: currentMonth, d: currentDay } = getTodayParts(timeZone);

  const personalYear = reduceToSingleDigit(birthDay + birthMonth + currentYear);
  const personalMonth = reduceToSingleDigit(personalYear + currentMonth);
  const personalDay = reduceToSingleDigit(personalMonth + currentDay);

  return personalDay === 0 ? 9 : personalDay;
}

export default function NumerologyClient() {
  const [birthDate, setBirthDate] = useState('');
  const [dayNumber, setDayNumber] = useState<number | null>(null);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement | null>(null);

  // max для input, чтобы нельзя выбрать будущую дату (по Томску)
  const maxDate = useMemo(() => {
    const { y, m, d } = getTodayParts('Asia/Tomsk');
    const mm = String(m).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }, []);

  const numerologyResult = dayNumber ? (NUM_MAP[dayNumber] ?? null) : null;

  const calculate = useCallback((date: string) => {
    if (!date) {
      setError('Пожалуйста, введите дату рождения.');
      setDayNumber(null);
      return;
    }

    const [by, bm, bd] = date.split('-').map(Number);
    if (!by || !bm || !bd) {
      setError('Некорректная дата.');
      setDayNumber(null);
      return;
    }

    // Валидация “не в будущем” — сравниваем YMD по Томску
    const birthNum = toYMDNumber(by, bm, bd);
    const t = getTodayParts('Asia/Tomsk');
    const todayNum = toYMDNumber(t.y, t.m, t.d);

    if (birthNum > todayNum) {
      setError('Дата рождения не может быть в будущем.');
      setDayNumber(null);
      return;
    }

    setError('');
    const num = getPersonalDayNumber(date, 'Asia/Tomsk');
    setDayNumber(num);

    try {
      localStorage.setItem('userBirthDate', date);
    } catch {}
  }, []);

  // Подтягиваем дату из localStorage один раз
  useEffect(() => {
    try {
      const stored = localStorage.getItem('userBirthDate');
      if (stored) {
        setBirthDate(stored);
        calculate(stored);
      }
    } catch {}
  }, [calculate]);

  // Автоскролл к результату (без setTimeout)
  useEffect(() => {
    if (!numerologyResult) return;
    const id = requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [numerologyResult]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      calculate(birthDate);
    },
    [birthDate, calculate],
  );

  const handleReset = useCallback(() => {
    setBirthDate('');
    setDayNumber(null);
    setError('');
    try {
      localStorage.removeItem('userBirthDate');
    } catch {}
  }, []);

  return (
    <>
      <style jsx>{`
        .reveal {
          animation: reveal 420ms ease-out;
        }
        @keyframes reveal {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal {
            animation: none !important;
            transform: none !important;
          }
        }
      `}</style>

      <div className="bg-gradient-to-b from-indigo-900 to-black rounded-2xl text-white p-4 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-main">Нумерология</h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
            Введите свою дату рождения, чтобы рассчитать персональное Число Дня.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto justify-center"
        >
          <label htmlFor="birthdate" className="sr-only">
            Дата рождения
          </label>
          <input
            id="birthdate"
            type="date"
            value={birthDate}
            max={maxDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 w-full sm:w-auto flex-grow focus:outline-none focus:ring-2 focus:ring-yellow-400"
            style={{ colorScheme: 'dark' }}
          />
          <button
            type="submit"
            className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            Рассчитать
          </button>
        </form>

        {error && (
          <div className="text-center text-red-400 mt-4">
            <p role="alert">{error}</p>
          </div>
        )}

        {numerologyResult && (
          <div
            ref={resultRef}
            className="mt-12 max-w-2xl mx-auto bg-black/30 p-6 sm:p-8 rounded-2xl border border-gray-700 reveal"
            role="region"
            aria-live="polite"
          >
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-300">
                Ваше персональное число на сегодня:
              </h2>
              <div className="text-7xl sm:text-8xl font-bold text-yellow-400 my-4">
                {numerologyResult.number}
              </div>
              <p className="text-lg sm:text-xl font-semibold text-white mb-4">
                {numerologyResult.keywords}
              </p>
            </div>

            <p className="text-base sm:text-lg text-gray-200 leading-relaxed whitespace-pre-line">
              {numerologyResult.description}
            </p>

            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-red-400 transition-colors mt-6 block mx-auto focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
            >
              Сбросить дату
            </button>
          </div>
        )}
      </div>
    </>
  );
}
